import { useState, useEffect } from 'react';
import { doctorsApi, departmentsApi, formatApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/layout/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Pencil, Trash2, Stethoscope, Loader2, LogIn } from 'lucide-react';
import { toast } from 'sonner';

const DAYS = [
  { key: 'monday', label: 'Lunedì' },
  { key: 'tuesday', label: 'Martedì' },
  { key: 'wednesday', label: 'Mercoledì' },
  { key: 'thursday', label: 'Giovedì' },
  { key: 'friday', label: 'Venerdì' },
  { key: 'saturday', label: 'Sabato' }
];

export default function Doctors() {
  const { isAdmin, user, token } = useAuth();

  const API = process.env.REACT_APP_BACKEND_URL;

  const handleImpersonateDoctor = async (doctorId, doctorName) => {
    try {
      const res = await fetch(`${API}/api/auth/impersonate-doctor/${doctorId}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        window.open(`${window.location.origin}/medico?impersonate_token=${data.access_token}`, '_blank');
        toast.success(`Portale Dr. ${doctorName} aperto in una nuova scheda`);
      } else { toast.error(data.detail || 'Errore'); }
    } catch { toast.error('Errore di rete'); }
  };
  const [doctors, setDoctors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    department_id: '',
    specialization: '',
    working_hours: {},
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [docsRes, deptsRes] = await Promise.all([
        doctorsApi.list(),
        departmentsApi.list()
      ]);
      setDoctors(docsRes.data);
      setDepartments(deptsRes.data.filter(d => d.is_active));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (doc = null) => {
    if (doc) {
      setEditingDoc(doc);
      setFormData({
        name: doc.name,
        email: doc.email,
        phone: doc.phone,
        department_id: doc.department_id,
        specialization: doc.specialization,
        working_hours: doc.working_hours || {},
        is_active: doc.is_active
      });
    } else {
      setEditingDoc(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        department_id: '',
        specialization: '',
        working_hours: {},
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const updateWorkingHours = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      working_hours: {
        ...prev.working_hours,
        [day]: {
          ...prev.working_hours[day],
          [field]: value
        }
      }
    }));
  };

  const toggleDay = (day, enabled) => {
    if (enabled) {
      setFormData(prev => ({
        ...prev,
        working_hours: {
          ...prev.working_hours,
          [day]: { start: '09:00', end: '17:00' }
        }
      }));
    } else {
      setFormData(prev => {
        const newHours = { ...prev.working_hours };
        delete newHours[day];
        return { ...prev, working_hours: newHours };
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingDoc) {
        await doctorsApi.update(editingDoc.id, formData);
        toast.success('Medico aggiornato');
      } else {
        await doctorsApi.create(formData);
        toast.success('Medico creato');
      }
      
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Eliminare il medico "${doc.name}"?`)) return;
    
    try {
      await doctorsApi.delete(doc.id);
      toast.success('Medico eliminato');
      loadData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Layout>
      <PageHeader 
        title="Medici" 
        subtitle="Gestisci i medici e i loro orari di lavoro"
        actions={
          isAdmin && (
            <Button 
              onClick={() => openDialog()} 
              className="bg-sky-500 hover:bg-sky-600"
              data-testid="add-doctor-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Medico
            </Button>
          )
        }
      />

      <div className="content-area" data-testid="doctors-list">
        <div className="table-container">
          <Table>
            <TableHeader className="table-header">
              <TableRow>
                <TableHead>Medico</TableHead>
                <TableHead>Reparto</TableHead>
                <TableHead>Specializzazione</TableHead>
                <TableHead>Contatti</TableHead>
                <TableHead>Stato</TableHead>
                {isAdmin && <TableHead className="text-right">Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </TableCell>
                </TableRow>
              ) : doctors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    <Stethoscope className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    Nessun medico trovato
                  </TableCell>
                </TableRow>
              ) : (
                doctors.map((doc) => (
                  <TableRow key={doc.id} data-testid={`doctor-row-${doc.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-emerald-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-emerald-600">
                            {doc.name.charAt(0)}
                          </span>
                        </div>
                        <span className="font-medium text-slate-800">{doc.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {doc.department_name || '-'}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {doc.specialization}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className="text-slate-600">{doc.email}</p>
                        <p className="text-slate-500">{doc.phone}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`badge ${doc.is_active ? 'badge-confirmed' : 'badge-cancelled'}`}>
                        {doc.is_active ? 'Attivo' : 'Inattivo'}
                      </span>
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        {user?.role === 'superadmin' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleImpersonateDoctor(doc.id, doc.name)}
                            className="text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                            title={`Accedi come Dr. ${doc.name}`}
                            data-testid={`impersonate-doctor-${doc.id}`}
                          >
                            <LogIn className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDialog(doc)}
                          data-testid={`edit-doctor-${doc.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(doc)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-doctor-${doc.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="doctor-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {editingDoc ? 'Modifica Medico' : 'Nuovo Medico'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Nome Completo *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Dr. Mario Rossi"
                  required
                  className="mt-1.5"
                  data-testid="doctor-name-input"
                />
              </div>
              <div>
                <Label htmlFor="specialization">Specializzazione *</Label>
                <Input
                  id="specialization"
                  value={formData.specialization}
                  onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                  placeholder="es. Odontoiatra"
                  required
                  className="mt-1.5"
                  data-testid="doctor-spec-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="medico@esempio.it"
                  required
                  className="mt-1.5"
                  data-testid="doctor-email-input"
                />
              </div>
              <div>
                <Label htmlFor="phone">Telefono *</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+39 333 1234567"
                  required
                  className="mt-1.5"
                  data-testid="doctor-phone-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="department">Reparto *</Label>
              <Select
                value={formData.department_id}
                onValueChange={(value) => setFormData({ ...formData, department_id: value })}
              >
                <SelectTrigger className="mt-1.5" data-testid="doctor-dept-select">
                  <SelectValue placeholder="Seleziona reparto" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Working Hours */}
            <div>
              <Label className="mb-3 block">Orari di Lavoro</Label>
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                {DAYS.map((day) => (
                  <div key={day.key} className="flex items-center gap-4 p-3">
                    <div className="w-24">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={!!formData.working_hours[day.key]}
                          onCheckedChange={(checked) => toggleDay(day.key, checked)}
                          data-testid={`day-switch-${day.key}`}
                        />
                        <span className="text-sm text-slate-700">{day.label}</span>
                      </div>
                    </div>
                    {formData.working_hours[day.key] && (
                      <div className="flex items-center gap-2">
                        <Input
                          type="time"
                          value={formData.working_hours[day.key]?.start || '09:00'}
                          onChange={(e) => updateWorkingHours(day.key, 'start', e.target.value)}
                          className="w-32"
                          data-testid={`time-start-${day.key}`}
                        />
                        <span className="text-slate-400">-</span>
                        <Input
                          type="time"
                          value={formData.working_hours[day.key]?.end || '17:00'}
                          onChange={(e) => updateWorkingHours(day.key, 'end', e.target.value)}
                          className="w-32"
                          data-testid={`time-end-${day.key}`}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="doctor-active-switch"
              />
              <Label htmlFor="is_active">Medico attivo</Label>
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Annulla
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                className="bg-sky-500 hover:bg-sky-600"
                data-testid="doctor-submit-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  editingDoc ? 'Aggiorna' : 'Crea Medico'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
